import type { InfectionSourceEvidence } from '../../services/api/types.gen';

const reconciliationLabels = {
  unverified: 'Reconciliation not checked',
  unmatched: 'Incomplete: no matching imported medication line',
  ambiguous: 'Incomplete: conflicting source identity',
  awaiting_reconciliation: 'Awaiting clinician reconciliation',
  reconciled: 'Reconciled by clinician',
  not_carried: 'Not carried forward by clinician',
};

export function InfectionSourceNotice({
  source,
}: {
  source: InfectionSourceEvidence;
}) {
  return (
    <section
      aria-label='Historical External EMR source'
      className='rounded-lg border bg-muted/20 px-4 py-3 space-y-2 text-sm'
    >
      <p className='font-medium'>Historical External EMR source</p>
      <p>
        {source.closed === true
          ? 'Closed in External EMR'
          : source.closed === false
            ? 'Open in External EMR'
            : 'Source closure unavailable'}
        . Source closure does not establish clinical resolution.
      </p>
      <dl className='grid grid-cols-[auto_1fr] gap-x-4 gap-y-1'>
        <dt className='text-muted-foreground'>Source record</dt>
        <dd>{source.record_key}</dd>
        <dt className='text-muted-foreground'>Infection type</dt>
        <dd>
          {source.type_description}
          {source.type_label ? `: ${source.type_label}` : ''}
          {source.type_code != null ? ` (source code ${source.type_code})` : ''}
        </dd>
        <dt className='text-muted-foreground'>Reported in source</dt>
        <dd>
          {source.reported_at ?? 'Unavailable'}
          {source.reported_at ? ' (source time zone not supplied)' : ''}
        </dd>
        <dt className='text-muted-foreground'>Captured</dt>
        <dd>{source.captured_at ?? 'Unavailable'}</dd>
      </dl>
      {Object.keys(source.staff_source_keys).length > 0 && (
        <details>
          <summary className='cursor-pointer'>
            Source attribution details
          </summary>
          <p className='text-muted-foreground'>
            Source identifiers only; no Roger assignment was inferred.
          </p>
          <dl>
            {Object.entries(source.staff_source_keys).map(([role, key]) => (
              <div key={role} className='flex gap-2'>
                <dt>{role}</dt>
                <dd>{key}</dd>
              </div>
            ))}
          </dl>
        </details>
      )}
      <details>
        <summary className='cursor-pointer'>
          Historical follow-up context
        </summary>
        <dl className='grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 mt-2'>
          <dt>Source closed time</dt>
          <dd>{source.closed_at ?? 'Unavailable'}</dd>
          <dt>Source follow-up date</dt>
          <dd>{source.follow_up_due_at ?? 'Unavailable'}</dd>
          <dt>Source assignee identifier</dt>
          <dd>{source.follow_up_assignee_source_key ?? 'Unavailable'}</dd>
        </dl>
        <p className='text-muted-foreground'>
          Historical source values only. No active follow-up assignment or
          reminder was created.
        </p>
      </details>
      <section
        aria-label='Historical infection medications'
        className='border-t pt-3 space-y-2'
      >
        <h4 className='font-medium'>
          Medications recorded with this historical infection
        </h4>
        {source.medications_status !== 'complete' && (
          <p className='text-muted-foreground'>
            Source medication evidence is incomplete or unavailable.
          </p>
        )}
        {source.medications_status === 'complete' &&
          !source.medications?.length && (
            <p className='text-muted-foreground'>
              No medications recorded with this source infection.
            </p>
          )}
        {(source.medications ?? []).map((medication, index) => (
          <article
            key={`${medication.registry_key ?? 'unidentified'}-${index}`}
            className='border-l-2 pl-3 space-y-1'
          >
            <p className='font-medium'>{medication.name}</p>
            <dl className='grid grid-cols-[auto_1fr] gap-x-4 gap-y-1'>
              <dt>Source strength</dt>
              <dd>{medication.strength || 'Unavailable'}</dd>
              <dt>Source instructions</dt>
              <dd className='whitespace-pre-wrap'>
                {medication.instructions || 'Unavailable'}
              </dd>
              <dt>Source start date</dt>
              <dd>{medication.start_date ?? 'Unavailable'}</dd>
              <dt>Source discontinue date</dt>
              <dd>{medication.discontinue_date ?? 'Unavailable'}</dd>
            </dl>
            <p>
              Current reconciliation disposition:{' '}
              {reconciliationLabels[medication.reconciliation ?? 'unverified']}
            </p>
          </article>
        ))}
        <p className='text-muted-foreground'>
          Review matched lines in Medications. Historical entries do not
          establish current medication use or why they were prescribed.
        </p>
      </section>
      <section
        aria-label='Historical source follow-up notes'
        className='border-t pt-3 space-y-2'
      >
        <h4 className='font-medium'>Historical source follow-up notes</h4>
        {source.notes_status !== 'complete' && (
          <p className='text-muted-foreground'>
            {source.notes_status === 'partial'
              ? 'Some source notes could not be verified or contain conflicting observations. Review the original source record.'
              : 'Source follow-up notes are unavailable.'}
          </p>
        )}
        {source.notes_status === 'complete' &&
          source.follow_up_notes.length === 0 && (
            <p className='text-muted-foreground'>
              No follow-up notes in this source snapshot.
            </p>
          )}
        {source.follow_up_notes.map((note, index) => (
          <article
            key={`${note.record_key ?? 'unidentified'}-${index}`}
            className='border-l-2 pl-3 space-y-1'
          >
            {note.conflicting && (
              <p className='font-medium'>Conflicting source observation</p>
            )}
            <p className='whitespace-pre-wrap'>{note.note}</p>
            <p className='text-xs text-muted-foreground'>
              Source modified date: {note.modified_at ?? 'Unavailable'}
            </p>
            <p className='text-xs text-muted-foreground'>
              Completed by in External EMR:{' '}
              {note.completed_by_source_name || 'Source attribution unresolved'}
            </p>
            <details className='text-xs text-muted-foreground'>
              <summary className='cursor-pointer'>
                Source note identifiers
              </summary>
              <p>
                Note: {note.record_key ?? 'Unavailable'} · Completed-by key:{' '}
                {note.completed_by_source_key ?? 'Unavailable'}. No Roger author
                or assignment inferred.
              </p>
            </details>
          </article>
        ))}
      </section>
      <p className='text-muted-foreground'>
        Historical follow-up assignments are not activated by import. Missing
        source values remain unknown until documented.
      </p>
    </section>
  );
}
